import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { exportTurfData } from '@api/survey_dashboard';
import { Button, ModalV2 as Modal, Input, Pill, isMaxNameLength } from '@utilities';

import { getAppliedFiltersModal } from './utilities/helpers';
import { MAX_SET_SIZE, MIN_SET_SIZE } from './utilities/constants';

import styles from './_index.module.scss';

const ModalDownloadBestSets = ({
  exportQuery,
  globalFilters,
  isActive,
  onClose,
  survey,
  surveyQuestions,
}) => {
  const [searchParams] = useSearchParams();
  const params = useParams();

  const appliedFilters = useMemo(
    () =>
      getAppliedFiltersModal({
        globalFilters,
        searchParams,
        survey,
        surveyQuestions,
      }) || {},
    [globalFilters, searchParams, survey, surveyQuestions]
  );

  const [name, setName] = useState(() => survey?.title || '');
  const [setSize, setSetSize] = useState(() => 5);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const trimmedName = name.trim();
  const nameTooLong = isMaxNameLength(trimmedName);

  const handleSetSizeChange = (val) => {
    if (val === '' || val === null || val === undefined) {
      setSetSize('');
      return;
    }
    const number = Number(val);
    setSetSize(Number.isNaN(number) ? '' : number);
  };

  const isInteger = (n) => Number.isInteger(n);
  const validateSetSize = (number) => number < MIN_SET_SIZE || number > MAX_SET_SIZE;

  const nonInteger =
    setSize !== '' && !Number.isNaN(Number(setSize)) && !isInteger(Number(setSize));
  const invalidSize = setSize !== '' && validateSetSize(Number(setSize));

  const createDisabled = nameTooLong || setSize === '' || invalidSize || nonInteger || isLoading;

  const handleCreate = async () => {
    setError(null);

    const itemSetSize = Number(setSize);
    if (nameTooLong || setSize === '' || nonInteger || validateSetSize(itemSetSize)) {
      return;
    }

    const surveyId = params.survey_id;
    const questionSourceId = surveyQuestions?.[0]?.questionSourceId;

    try {
      setIsLoading(true);
      const query = {
        ...exportQuery,
        combination_size: itemSetSize,
        custom_filename: trimmedName,
        question_source_id: questionSourceId,
        survey_id: surveyId,
      };

      await exportTurfData({
        query,
      });

      onClose?.();
    } catch (err) {
      setError(err);
      console.warn(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      buttons={[
        <Button key="cancel" onClick={onClose} text="Cancel" variant="secondary" />,
        <Button
          isDisabled={createDisabled}
          isLoading={isLoading}
          key="create"
          onClick={handleCreate}
          text="Create"
        />,
      ]}
      isActive={isActive}
      isScrollable
      onClose={onClose}
      title="Download Best Sets"
    >
      <p className={styles['modal-dbs-subheading']}>
        An Excel output will be created to display multiple sets based on number of items in a set
        selected.{' '}
        <a
          href="https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf"
          rel="noreferrer"
          target="_blank"
        >
          Learn more
        </a>
      </p>
      <Input
        className={`${styles['modal-dbs-input-name']} ${nameTooLong ? styles['modal-dbs-input-name-error'] : ''}`}
        error={nameTooLong ? 'Maximum number of characters reached. Try something shorter.' : ''}
        label="NAME"
        onChange={setName}
        value={name}
      />

      <Input
        className={styles['modal-dbs-set-size']}
        error={
          setSize === ''
            ? ''
            : nonInteger
              ? 'Number of items in a set must be a whole number.'
              : invalidSize
                ? `Number of items in a set must be between ${MIN_SET_SIZE} and ${MAX_SET_SIZE}.`
                : ''
        }
        isRequired
        label="NUMBER OF ITEMS IN A SET (SIZE)"
        max={MAX_SET_SIZE}
        min={MIN_SET_SIZE}
        onChange={handleSetSizeChange}
        type="number"
        value={setSize}
      />
      <small className={styles['modal-dbs-set-size-text']}>
        Must be between {MIN_SET_SIZE} and {MAX_SET_SIZE}.
      </small>

      {error ? (
        <div className={styles['modal-dbs-error']} role="alert">
          {error?.message || 'Something went wrong while creating the export.'}
        </div>
      ) : null}

      <hr />
      <div>
        {Object.entries(appliedFilters)
          .filter(
            ([section, items]) =>
              (section === 'Demographic Filters' || section === 'Quota Group Filters') &&
              items?.length
          )
          .map(([section, items]) => (
            <div className={styles['modal-dbs-applied-filters']} key={section}>
              <h4>{section}</h4>
              <div className={styles['modal-dbs-pill-row']}>
                {items.map((label) => (
                  <Pill
                    className={styles['modal-dbs-pill']}
                    isCompleted
                    key={`${section}:${String(label)}`}
                    value={label}
                  />
                ))}
              </div>
            </div>
          ))}
        {appliedFilters['Question Option Filters']?.length ? (
          <div className={styles['modal-dbs-applied-filters']}>
            <h4>Question Filters</h4>
            <div className={styles['modal-dbs-question-filters']}>
              {appliedFilters['Question Option Filters'].map((question) => (
                <div key={question}>
                  <Pill isCompleted value={question} />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

ModalDownloadBestSets.propTypes = {
  exportQuery: PropTypes.object.isRequired,
  globalFilters: PropTypes.object,
  isActive: PropTypes.bool,
  onClose: PropTypes.func,
  survey: PropTypes.object,
  surveyQuestions: PropTypes.array,
};

export default ModalDownloadBestSets;
